/**
 * ClickReel SDK - Instagram Player Overlay
 *
 * Supports two modes:
 * 1. Direct MP4 URL - Full HTML5 video control with progress tracking
 * 2. Instagram Embed iframe - Simple display with Instagram's native player
 */

import {
  VideoProgressData,
  BubblePosition,
  StylingConfig,
  FloatingAction,
  ProductCardConfig,
  SocialProofConfig,
} from './types';
import { getShadowContainer } from './shadow-dom';
import { ProductCard } from './product-card';
import {
  createUnmuteOverlay,
  getConnectionQuality,
  createBufferingOverlay,
  createTapToLoadPlaceholder,
  injectUtilityStyles,
  getInstagramEmbedUrl,
  isMobile,
} from './video-utils';
import { buildCtaUrl } from './analytics-bridge';

// =============================================================================
// Instagram Player Component
// =============================================================================

export interface InstagramPlayerOptions {
  /** Instagram post URL for iframe embed mode (e.g., https://www.instagram.com/p/DR0wEN5jS4J/) */
  postUrl?: string;
  /** Direct MP4 URL for full HTML5 control */
  videoUrl?: string;
  position: BubblePosition;
  styling: StylingConfig;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (data: VideoProgressData) => void;
  onComplete?: () => void;
  onClose?: () => void;
  onCtaClick?: () => void;
  onFloatingActionClick?: (action: FloatingAction) => void;
  ctaText?: string;
  ctaUrl?: string;
  floatingActions?: FloatingAction[];
  muteOnOpen?: boolean;
  productCard?: ProductCardConfig;
  socialProof?: SocialProofConfig;
  storyMode?: boolean;
  storyIndex?: number;
  storyTotal?: number;
  onStoryAdvance?: (direction: 'next' | 'prev') => void;
  showBranding?: boolean;
  estimatedDuration?: number;
  campaignName?: string;
  sessionId?: string;
  videoIdForTracking?: string;
}

/**
 * Instagram player overlay component with dual mode support
 */
export class InstagramPlayer {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private options: InstagramPlayerOptions;
  private isOpen = false;
  private lastQuartile = 0;
  private progressInterval: number | null = null;
  private mode: 'mp4' | 'embed';
  private productCardInstance: ProductCard | null = null;
  private bufferingOverlay: HTMLElement | null = null;
  private closing = false;
  private destroyed = false;
  private embedProgressInterval: number | null = null;
  private embedStartTime: number = 0;
  // Sprint 005 (2026-04-29): visibility-aware progress for embed mode.
  // Same rationale as tiktok-player.ts — Date.now() advances in hidden tabs
  // but the iframe pauses, so unguarded elapsed accumulator inflates progress.
  private embedVisibilityHandler: (() => void) | null = null;
  private embedPausedAt: number = 0;
  private embedLastQuartile: number = 0;

  constructor(options: InstagramPlayerOptions) {
    this.options = options;
    // Determine mode: MP4 takes priority if both provided
    this.mode = options.videoUrl ? 'mp4' : 'embed';
  }

  /**
   * Open the video player
   */
  async open(): Promise<void> {
    if (this.isOpen) return;

    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    this.render(shadow);
    this.isOpen = true;

    // Start video playback for MP4 mode (skip autoplay on slow connections)
    const quality = getConnectionQuality();
    if (this.mode === 'mp4' && this.videoElement && quality !== 'slow') {
      requestAnimationFrame(() => {
        if (this.options.muteOnOpen === false && this.videoElement) {
          // Try unmuted play first (works after user gesture like bubble click)
          this.videoElement.play().catch(() => {
            // Unmuted autoplay blocked — fall back to muted
            if (this.videoElement) {
              this.videoElement.muted = true;
              this.videoElement.play().catch(() => {});
              const muteBtn = this.container?.querySelector('.clickreel-mute-btn');
              if (muteBtn) muteBtn.innerHTML = this.getMuteIcon();
            }
          });
        } else {
          this.videoElement?.play().catch(() => {});
        }
      });
    }
  }

  /**
   * Close the video player
   */
  close(): void {
    if (!this.isOpen || !this.overlay) return;
    this.closing = true;

    this.stopProgressTracking();
    this.stopEmbedProgressTracking();

    // Animate out
    this.overlay.classList.add('closing');

    setTimeout(() => {
      this.destroy();
      this.options.onClose?.();
    }, 200);
  }

  /**
   * Toggle play/pause (only works in MP4 mode)
   */
  togglePlay(): void {
    if (this.mode !== 'mp4' || !this.videoElement) return;

    if (this.videoElement.paused) {
      this.videoElement.play();
    } else {
      this.videoElement.pause();
    }
  }

  /**
   * Check if player is currently open
   */
  get isVisible(): boolean {
    return this.isOpen;
  }

  /**
   * Destroy the player and clean up
   */
  destroy(): void {
    this.destroyed = true;
    this.closing = true;
    this.stopProgressTracking();
    this.stopEmbedProgressTracking();
    this.productCardInstance?.destroy();
    this.productCardInstance = null;
    this.bufferingOverlay = null;
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = '';
      this.videoElement = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.container = null;
    }
    this.isOpen = false;
    this.lastQuartile = 0;
    this.embedLastQuartile = 0;
    this.embedStartTime = 0;

    // Remove escape key handler
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  // ===========================================================================
  // Private Methods - Rendering
  // ===========================================================================

  private render(shadow: ShadowRoot): void {
    const mobile = isMobile();

    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay clickreel-instagram-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Instagram video player');

    // Click outside to close (on backdrop) — desktop only
    // On mobile iOS, touch events in Shadow DOM can misfire and close the player
    if (!mobile) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });
    }

    // Create container
    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    // Apply position-based styles for desktop PiP
    if (!mobile) {
      this.applyPiPStyles();
    }

    // Build player content
    this.container.appendChild(this.createCloseButton());

    // Create video container
    const videoWrapper = this.createVideoContainer();

    // Feature 3: Story mode UI
    if (this.options.storyMode) {
      this.createStoryUI(videoWrapper);
    }

    // Feature 8: Social proof
    if (this.options.socialProof?.enabled) {
      this.createSocialProof(videoWrapper);
    }

    // Feature 4: Product card
    if (this.options.productCard?.enabled) {
      this.productCardInstance = new ProductCard(
        this.options.productCard,
        this.options.styling.primaryColor || '#E1306C',
      );
      this.productCardInstance.render(videoWrapper);
    }

    this.container.appendChild(videoWrapper);

    // For embed mode: floating actions go BELOW the video wrapper, not inside it
    // For MP4 mode: floating actions go inside the wrapper (overlay on video is expected)
    if (this.options.floatingActions && this.options.floatingActions.length > 0) {
      if (this.mode === 'embed') {
        this.container.appendChild(this.createFloatingActions());
      } else {
        videoWrapper.appendChild(this.createFloatingActions());
      }
    }

    // Add controls for MP4 mode on desktop
    if (this.mode === 'mp4' && !mobile) {
      this.container.appendChild(this.createVideoControls());
    }

    if (this.options.showBranding) {
      const watermark = document.createElement('a');
      watermark.href = 'https://clickreel.io?utm_source=widget&utm_medium=referral&utm_campaign=powered_by';
      watermark.target = '_blank';
      watermark.rel = 'noopener';
      watermark.textContent = 'Powered by ClickReel.io';
      watermark.className = 'clickreel-player-watermark';
      videoWrapper.appendChild(watermark);
    }

    // Only show CTA on desktop if provided
    if (!mobile && this.options.ctaText && this.options.ctaUrl) {
      this.container.appendChild(this.createCTAButton());
    }

    this.overlay.appendChild(this.container);

    // Inject styles
    this.injectStyles(shadow);

    shadow.appendChild(this.overlay);

    // Handle escape key
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  private createCloseButton(): HTMLElement {
    const button = document.createElement('button');
    button.className = 'clickreel-close-btn';
    button.setAttribute('aria-label', 'Close video');
    button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.3 5.71a.996.996 0 00-1.41 0L12 10.59 7.11 5.7A.996.996 0 105.7 7.11L10.59 12 5.7 16.89a.996.996 0 101.41 1.41L12 13.41l4.89 4.89a.996.996 0 101.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
      </svg>
    `;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    return button;
  }

  private createVideoContainer(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';
    // MP4 mode uses portrait aspect ratio; embed mode uses its own sizing
    wrapper.setAttribute('data-orientation', this.mode === 'mp4' ? 'portrait' : 'embed');

    if (this.mode === 'mp4') {
      // Direct MP4 mode - full HTML5 video control
      wrapper.appendChild(this.createMP4Player());
    } else {
      // Instagram embed iframe mode
      wrapper.appendChild(this.createEmbedIframe());
    }

    return wrapper;
  }

  private createMP4Player(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-instagram-video-container';

    // Feature 11: Adaptive quality - check connection before creating video
    const quality = getConnectionQuality();

    this.videoElement = document.createElement('video');
    this.videoElement.className = 'clickreel-instagram-video';
    this.videoElement.src = this.options.videoUrl || '';
    this.videoElement.playsInline = true;
    this.videoElement.muted = this.options.muteOnOpen !== false; // Respect muteOnOpen option
    this.videoElement.loop = false;
    this.videoElement.setAttribute('playsinline', 'true');
    this.videoElement.setAttribute('webkit-playsinline', 'true');

    // Feature 11: Adaptive quality settings based on connection
    if (quality === 'medium') {
      this.videoElement.preload = 'metadata';
    } else if (quality === 'slow') {
      this.videoElement.preload = 'none';
    } else {
      this.videoElement.preload = 'auto';
    }

    // Event listeners
    this.videoElement.addEventListener('play', () => {
      this.options.onPlay?.();
      this.updatePlayButton(true);
      this.startProgressTracking();
    });

    this.videoElement.addEventListener('pause', () => {
      this.options.onPause?.();
      this.updatePlayButton(false);
    });

    this.videoElement.addEventListener('ended', () => {
      if (this.destroyed || this.closing) return;
      this.options.onComplete?.();
      this.updatePlayButton(false);
      this.stopProgressTracking();
      this.emitProgress(100);

      // Feature 3: Story mode - advance to next story on video end
      if (this.options.storyMode && !this.closing && !this.destroyed) {
        this.options.onStoryAdvance?.('next');
      }
    });

    this.videoElement.addEventListener('loadedmetadata', () => {
      this.startProgressTracking();
    });

    // Feature 1: Muted autoplay + tap-to-unmute
    this.videoElement.addEventListener(
      'playing',
      () => {
        if (this.videoElement?.muted) {
          const videoContainer = this.videoElement.parentElement;
          if (videoContainer) {
            createUnmuteOverlay(() => {
              if (this.videoElement) {
                this.videoElement.muted = false;
                const muteBtn = this.container?.querySelector('.clickreel-mute-btn');
                if (muteBtn) muteBtn.innerHTML = this.getVolumeIcon();
              }
            }, videoContainer);
          }
        }
      },
      { once: true },
    );

    // Feature 11: Adaptive quality - buffering overlay for medium connections
    if (quality === 'medium') {
      this.videoElement.addEventListener('waiting', () => {
        if (!this.bufferingOverlay && container) {
          this.bufferingOverlay = createBufferingOverlay(container);
        }
        if (this.bufferingOverlay) {
          this.bufferingOverlay.classList.remove('hidden');
        }
      });
      this.videoElement.addEventListener('playing', () => {
        if (this.bufferingOverlay) {
          this.bufferingOverlay.classList.add('hidden');
        }
      });
    }

    container.appendChild(this.videoElement);

    // MP4→embed fallback: if MP4 fails to load, seamlessly switch to embed mode.
    // Uses both error event AND a timeout since <video> error events don't fire
    // reliably for non-video URLs (HTML pages, CORS blocks).
    let mp4Loaded = false;
    const switchToEmbed = () => {
      if (mp4Loaded || !this.options.postUrl || this.destroyed || this.closing) return;
      mp4Loaded = true; // prevent double-switch
      this.mode = 'embed';
      this.videoElement = null;
      container.innerHTML = '';
      const wrapper = container.parentElement;
      if (wrapper) {
        container.remove();
        wrapper.appendChild(this.createEmbedIframe());
        this.startEmbedProgressTracking();
      }
    };

    this.videoElement.addEventListener('error', switchToEmbed);

    // Mark as successfully loaded when we get real video metadata
    this.videoElement.addEventListener('loadeddata', () => { mp4Loaded = true; });

    // Timeout fallback: if no video data loads within 4s, switch to embed
    setTimeout(() => {
      if (!mp4Loaded && this.mode === 'mp4') {
        switchToEmbed();
      }
    }, 4000);

    // Feature 11: Adaptive quality - slow connection shows tap-to-load placeholder
    if (quality === 'slow') {
      const placeholder = createTapToLoadPlaceholder(() => {
        this.videoElement?.play().catch(() => {
          // Autoplay blocked - user needs to interact
        });
      });
      container.appendChild(placeholder);
    }

    return container;
  }

  private createEmbedIframe(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-instagram-embed-container';

    const postUrl = this.options.postUrl || '';
    const embedUrl = getInstagramEmbedUrl(postUrl);

    // Loader
    const loader = document.createElement('div');
    loader.className = 'clickreel-instagram-loader';
    loader.innerHTML = `
      <div class="clickreel-instagram-loader-spinner"></div>
      <span>Loading...</span>
    `;

    if (!embedUrl) {
      // Show fallback — no shortcode extractable
      const fallback = document.createElement('div');
      fallback.className = 'clickreel-instagram-fallback';
      fallback.innerHTML = `
        <div class="clickreel-instagram-fallback-icon">
          <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
        </div>
        <p>Video unavailable</p>
      `;
      container.appendChild(fallback);
      return container;
    }

    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.className = 'clickreel-instagram-iframe';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; unload';

    iframe.addEventListener('load', () => {
      loader.style.display = 'none';
      // Fire onPlay immediately so ClickReel's event tracking picks up the play event
      this.options.onPlay?.();
      this.startEmbedProgressTracking();
    });

    container.appendChild(loader);
    container.appendChild(iframe);

    return container;
  }

  private createVideoControls(): HTMLElement {
    const controls = document.createElement('div');
    controls.className = 'clickreel-video-controls clickreel-instagram-controls';

    // Play/Pause button
    const playBtn = document.createElement('button');
    playBtn.className = 'clickreel-play-btn';
    playBtn.innerHTML = this.getPauseIcon(); // Start with pause since autoplay
    playBtn.addEventListener('click', () => this.togglePlay());

    // Progress bar
    const progressContainer = document.createElement('div');
    progressContainer.className = 'clickreel-progress-container';

    const progressBar = document.createElement('div');
    progressBar.className = 'clickreel-progress-bar';

    const progressFill = document.createElement('div');
    progressFill.className = 'clickreel-progress-fill';

    progressBar.appendChild(progressFill);
    progressContainer.appendChild(progressBar);

    // Click on progress bar to seek
    progressContainer.addEventListener('click', (e) => {
      if (!this.videoElement) return;
      const rect = progressBar.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      this.videoElement.currentTime = percent * this.videoElement.duration;
    });

    // Time display
    const timeDisplay = document.createElement('span');
    timeDisplay.className = 'clickreel-time-display';
    timeDisplay.textContent = '0:00 / 0:00';

    // Volume/Mute button
    const muteBtn = document.createElement('button');
    muteBtn.className = 'clickreel-mute-btn';
    muteBtn.innerHTML = this.options.muteOnOpen !== false ? this.getMuteIcon() : this.getVolumeIcon();
    muteBtn.addEventListener('click', () => {
      if (!this.videoElement) return;
      this.videoElement.muted = !this.videoElement.muted;
      muteBtn.innerHTML = this.videoElement.muted ? this.getMuteIcon() : this.getVolumeIcon();
    });

    controls.appendChild(playBtn);
    controls.appendChild(progressContainer);
    controls.appendChild(timeDisplay);
    controls.appendChild(muteBtn);

    return controls;
  }

  private createCTAButton(): HTMLElement {
    const { styling } = this.options;

    const cta = document.createElement('a');
    cta.className = 'clickreel-cta-btn';
    cta.href = buildCtaUrl(this.options.ctaUrl, {
      campaignName: this.options.campaignName,
      videoId: this.options.videoIdForTracking,
      sessionId: this.options.sessionId,
    });
    cta.target = '_blank';
    cta.rel = 'noopener noreferrer';
    cta.textContent = this.options.ctaText || 'Learn More';
    cta.style.backgroundColor = styling.primaryColor || '#E1306C'; // Instagram pink

    cta.addEventListener('click', () => {
      this.options.onCtaClick?.();
    });

    return cta;
  }

  private createFloatingActions(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-floating-actions';

    this.options.floatingActions?.forEach((action) => {
      const actionBtn = document.createElement('a');
      actionBtn.className = 'clickreel-floating-action';
      actionBtn.href = action.url;
      actionBtn.target = '_blank';
      actionBtn.rel = 'noopener noreferrer';

      // Left side: Shopping bag icon with pulse animation
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'clickreel-floating-action-icon-wrap';

      if (action.imageUrl) {
        const img = document.createElement('img');
        img.src = action.imageUrl;
        img.alt = action.label;
        img.className = 'clickreel-floating-action-img';
        iconWrapper.appendChild(img);
      } else {
        const icon = document.createElement('div');
        icon.className = 'clickreel-floating-action-icon';
        icon.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zm-9-1a2 2 0 0 1 4 0v1h-4V6zm8 13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v10z"/>
          </svg>
        `;
        iconWrapper.appendChild(icon);
      }
      actionBtn.appendChild(iconWrapper);

      // Center: Product info
      const textContainer = document.createElement('div');
      textContainer.className = 'clickreel-floating-action-text';

      const label = document.createElement('span');
      label.className = 'clickreel-floating-action-label';
      label.textContent = action.label;
      textContainer.appendChild(label);

      if (action.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'clickreel-floating-action-subtitle';
        subtitle.textContent = action.subtitle;
        textContainer.appendChild(subtitle);
      }

      actionBtn.appendChild(textContainer);

      // Right side: Shop Now button
      const shopBtn = document.createElement('span');
      shopBtn.className = 'clickreel-floating-action-btn';
      shopBtn.textContent = 'Shop';
      actionBtn.appendChild(shopBtn);

      // Feature 15: Variant picker (appended to textContainer after shopBtn is created)
      if (action.variants && action.variants.length > 0) {
        const variantPicker = this.createVariantPicker(action, actionBtn, shopBtn);
        textContainer.appendChild(variantPicker);
      }

      actionBtn.addEventListener('click', () => {
        this.options.onFloatingActionClick?.(action);
      });

      container.appendChild(actionBtn);
    });

    return container;
  }

  /**
   * Feature 3: Create story mode UI with progress bar and navigation
   */
  private createStoryUI(wrapper: HTMLElement): void {
    const total = this.options.storyTotal ?? 1;
    const index = this.options.storyIndex ?? 0;

    // Story progress bar
    const bar = document.createElement('div');
    bar.className = 'clickreel-story-bar';
    for (let i = 0; i < total; i++) {
      const segment = document.createElement('div');
      segment.className = 'clickreel-story-segment';
      if (i < index) {
        segment.classList.add('completed');
      } else if (i === index) {
        segment.classList.add('active');
        const fill = document.createElement('div');
        fill.className = 'clickreel-story-fill';
        segment.appendChild(fill);
      }
      bar.appendChild(segment);
    }
    wrapper.appendChild(bar);

    // Story counter
    const counter = document.createElement('div');
    counter.className = 'clickreel-story-counter';
    counter.textContent = `${index + 1} / ${total}`;
    wrapper.appendChild(counter);

    // Left nav (tap to go prev)
    const navLeft = document.createElement('div');
    navLeft.className = 'clickreel-story-nav clickreel-story-nav-left';
    navLeft.innerHTML = `<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg></div>`;
    navLeft.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('prev');
    });
    wrapper.appendChild(navLeft);

    // Right nav (tap to go next)
    const navRight = document.createElement('div');
    navRight.className = 'clickreel-story-nav clickreel-story-nav-right';
    navRight.innerHTML = `<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/></svg></div>`;
    navRight.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('next');
    });
    wrapper.appendChild(navRight);
  }

  /**
   * Feature 8: Create social proof badge
   */
  private createSocialProof(wrapper: HTMLElement): void {
    const config = this.options.socialProof;
    if (!config) return;

    const badge = document.createElement('div');
    const pos = config.position || 'top-left';
    badge.className = `clickreel-social-proof ${pos}`;
    badge.textContent = config.text;
    wrapper.appendChild(badge);
  }

  /**
   * Feature 15: Create variant picker for a floating action
   */
  private createVariantPicker(
    action: FloatingAction,
    actionLink: HTMLAnchorElement,
    _shopBtn: HTMLSpanElement,
  ): HTMLElement {
    const picker = document.createElement('div');
    picker.className = 'clickreel-variant-picker';

    const MAX_VISIBLE = 5;
    const variants = action.variants || [];
    const visible = variants.slice(0, MAX_VISIBLE);
    const remaining = variants.length - MAX_VISIBLE;

    visible.forEach((variant, idx) => {
      if (variant.type === 'color' && variant.colorHex) {
        const swatch = document.createElement('div');
        swatch.className = 'clickreel-variant-swatch';
        if (idx === 0) swatch.classList.add('selected');
        if (variant.available === false) swatch.classList.add('unavailable');
        swatch.style.backgroundColor = variant.colorHex;
        swatch.title = variant.label;
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (variant.available === false) return;
          picker
            .querySelectorAll('.clickreel-variant-swatch')
            .forEach((s) => s.classList.remove('selected'));
          swatch.classList.add('selected');
          this.updateActionUrl(actionLink, action, variant.id);
          this.updateActionLabel(actionLink, variant.label);
        });
        picker.appendChild(swatch);
      } else {
        const chip = document.createElement('button');
        chip.className = 'clickreel-variant-chip';
        if (idx === 0) chip.classList.add('selected');
        if (variant.available === false) chip.classList.add('unavailable');
        chip.textContent = variant.label;
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (variant.available === false) return;
          picker
            .querySelectorAll('.clickreel-variant-chip')
            .forEach((c) => c.classList.remove('selected'));
          chip.classList.add('selected');
          this.updateActionUrl(actionLink, action, variant.id);
          this.updateActionLabel(actionLink, variant.label);
        });
        picker.appendChild(chip);
      }
    });

    if (remaining > 0) {
      const more = document.createElement('span');
      more.className = 'clickreel-variant-more';
      more.textContent = `+${remaining}`;
      picker.appendChild(more);
    }

    return picker;
  }

  /**
   * Feature 15: Update the href of a floating action when a variant is selected
   */
  private updateActionUrl(
    link: HTMLAnchorElement,
    action: FloatingAction,
    variantId: string,
  ): void {
    try {
      const url = new URL(action.url, window.location.origin);
      const paramName = action.selectedVariantParam || 'variant';
      url.searchParams.set(paramName, variantId);
      link.href = url.toString();
    } catch {
      // Invalid URL; leave href unchanged
    }
  }

  /**
   * Feature 15: Update the label text inside a floating action
   */
  private updateActionLabel(link: HTMLAnchorElement, _variantLabel: string): void {
    const subtitle = link.querySelector('.clickreel-floating-action-subtitle');
    if (subtitle) {
      // Keep original subtitle (price) unchanged - variant selection updates the URL only
    }
  }

  private applyPiPStyles(): void {
    if (!this.container) return;

    const { position } = this.options;
    const margin = '24px';

    let positionStyle = '';
    switch (position) {
      case 'bottom-right':
        positionStyle = `bottom: ${margin}; right: ${margin};`;
        break;
      case 'bottom-left':
        positionStyle = `bottom: ${margin}; left: ${margin};`;
        break;
      case 'top-right':
        positionStyle = `top: ${margin}; right: ${margin};`;
        break;
      case 'top-left':
        positionStyle = `top: ${margin}; left: ${margin};`;
        break;
    }

    this.container.style.cssText = positionStyle;
  }

  // ===========================================================================
  // Private Methods - Progress Tracking (MP4 mode only)
  // ===========================================================================

  private startProgressTracking(): void {
    if (this.mode !== 'mp4') return;

    this.stopProgressTracking();
    this.progressInterval = window.setInterval(() => {
      this.updateProgressUI();
      this.checkQuartiles();
    }, 500);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private startEmbedProgressTracking(): void {
    if (this.mode !== 'embed') return;
    this.stopEmbedProgressTracking();

    const duration = this.options.estimatedDuration || 300;
    this.embedStartTime = Date.now();
    this.embedLastQuartile = 0;
    this.embedPausedAt = 0;

    // Sprint 005 (2026-04-29): pause/resume embed elapsed on tab visibility
    this.embedVisibilityHandler = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        this.embedPausedAt = Date.now();
      } else if (this.embedPausedAt > 0) {
        const hiddenDuration = Date.now() - this.embedPausedAt;
        this.embedStartTime += hiddenDuration;
        this.embedPausedAt = 0;
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.embedVisibilityHandler);
    }

    this.embedProgressInterval = window.setInterval(() => {
      if (this.destroyed || this.closing) {
        this.stopEmbedProgressTracking();
        return;
      }

      const elapsed = (Date.now() - this.embedStartTime) / 1000;
      const percent = Math.min(Math.floor((elapsed / duration) * 100), 100);

      const quartiles: Array<25 | 50 | 75> = [25, 50, 75];
      for (const q of quartiles) {
        if (percent >= q && this.embedLastQuartile < q) {
          this.emitEmbedProgress(q, elapsed, duration);
          this.embedLastQuartile = q;
        }
      }

      if (this.productCardInstance && !this.productCardInstance.hasShown) {
        if (percent >= this.productCardInstance.triggerPercent) {
          this.productCardInstance.show();
        }
      }

      if (percent >= 100) {
        this.emitEmbedProgress(100, duration, duration);
        this.stopEmbedProgressTracking();
        // Embed durations are heuristic estimates — we can't know when the
        // video actually ends. In story mode, advance to the next video.
        // In single-video mode, let the user close manually (no auto-close).
        if (this.options.storyMode && !this.closing && !this.destroyed) {
          this.options.onComplete?.();
          this.options.onStoryAdvance?.('next');
        }
      }
    }, 500);
  }

  private stopEmbedProgressTracking(): void {
    if (this.embedProgressInterval) {
      clearInterval(this.embedProgressInterval);
      this.embedProgressInterval = null;
    }
    // Sprint 005 (2026-04-29): unbind embed visibility listener
    if (this.embedVisibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.embedVisibilityHandler);
      this.embedVisibilityHandler = null;
    }
    this.embedPausedAt = 0;
  }

  private emitEmbedProgress(quartile: 0 | 25 | 50 | 75 | 100, currentTime: number, duration: number): void {
    const data: VideoProgressData = {
      videoId: 'instagram-embed',
      currentTime: Math.round(currentTime * 100) / 100,
      duration,
      percent: Math.floor((currentTime / duration) * 100),
      quartile,
    };
    this.options.onProgress?.(data);
  }

  private updateProgressUI(): void {
    if (!this.videoElement || !this.container) return;

    const currentTime = this.videoElement.currentTime;
    const duration = this.videoElement.duration;

    if (!duration || isNaN(duration)) return;

    const percent = (currentTime / duration) * 100;

    // Update progress bar
    const fill = this.container.querySelector('.clickreel-progress-fill') as HTMLElement;
    if (fill) {
      fill.style.width = `${percent}%`;
    }

    // Update time display
    const timeDisplay = this.container.querySelector('.clickreel-time-display');
    if (timeDisplay) {
      timeDisplay.textContent = `${this.formatTime(currentTime)} / ${this.formatTime(duration)}`;
    }

    // Feature 12: Story progress - update story fill element width
    if (this.options.storyMode) {
      const storyFill = this.container.querySelector(
        '.clickreel-story-segment.active .clickreel-story-fill',
      ) as HTMLElement;
      if (storyFill) {
        storyFill.style.width = `${percent}%`;
      }
    }

    // Feature 4: Product card - show based on video progress
    if (this.productCardInstance && !this.productCardInstance.hasShown) {
      if (percent >= this.productCardInstance.triggerPercent) {
        this.productCardInstance.show();
      }
    }
  }

  private checkQuartiles(): void {
    if (!this.videoElement) return;

    const currentTime = this.videoElement.currentTime;
    const duration = this.videoElement.duration;

    if (!duration || isNaN(duration)) return;

    const percent = Math.floor((currentTime / duration) * 100);

    // Emit quartile events (25%, 50%, 75%)
    const quartiles = [25, 50, 75];
    for (const q of quartiles) {
      if (percent >= q && this.lastQuartile < q) {
        this.emitProgress(q as 25 | 50 | 75);
        this.lastQuartile = q;
      }
    }
  }

  private emitProgress(quartile: 0 | 25 | 50 | 75 | 100): void {
    if (!this.videoElement) return;

    const currentTime = this.videoElement.currentTime;
    const duration = this.videoElement.duration;

    const data: VideoProgressData = {
      videoId: 'instagram-video',
      currentTime,
      duration,
      percent: Math.floor((currentTime / duration) * 100),
      quartile,
    };

    this.options.onProgress?.(data);
  }

  private updatePlayButton(isPlaying: boolean): void {
    const btn = this.container?.querySelector('.clickreel-play-btn');
    if (btn) {
      btn.innerHTML = isPlaying ? this.getPauseIcon() : this.getPlayIcon();
    }
  }

  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  // ===========================================================================
  // Private Methods - Utilities
  // ===========================================================================

  private formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private getPlayIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
    </svg>`;
  }

  private getPauseIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>`;
  }

  private getVolumeIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>`;
  }

  private getMuteIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>`;
  }

  private injectStyles(shadow: ShadowRoot): void {
    // Feature 10: Inject shared utility styles (unmute, buffering, story, social proof, product card, variant picker)
    injectUtilityStyles(shadow);

    const existingStyle = shadow.querySelector('style[data-instagram-player]');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.setAttribute('data-instagram-player', 'true');
    style.textContent = `
      /* ================================================================
         Base overlay/container styles (Instagram player is standalone,
         so it must include these — video-player.ts won't inject them)
         ================================================================ */

      .clickreel-video-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: clickreel-fade-in 0.2s ease;
        pointer-events: auto;
        z-index: 2;
      }

      .clickreel-video-overlay.closing {
        animation: clickreel-fade-out 0.2s ease forwards;
      }

      .clickreel-video-container.desktop {
        position: absolute;
        width: 380px;
        max-width: 90vw;
        background: #000;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        animation: clickreel-slide-up 0.3s ease;
      }

      .clickreel-video-container.mobile {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background: #000;
        display: flex;
        flex-direction: column;
      }

      .clickreel-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 100;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .clickreel-close-btn:hover {
        background: rgba(0, 0, 0, 0.7);
      }

      /* Mobile: Larger touch target with contrast ring in safe area */
      .mobile .clickreel-close-btn {
        top: max(16px, env(safe-area-inset-top, 16px));
        right: max(16px, env(safe-area-inset-right, 16px));
        width: 44px;
        height: 44px;
        background: rgba(0, 0, 0, 0.6);
        border: 2px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      }

      .mobile .clickreel-close-btn:active {
        background: rgba(0, 0, 0, 0.8);
        border-color: rgba(255, 255, 255, 0.6);
      }

      .clickreel-video-wrapper {
        position: relative;
        width: 100%;
        background: #000;
      }

      .clickreel-video-wrapper[data-orientation="portrait"] {
        aspect-ratio: 9/16;
      }

      /* Embed mode: no forced aspect ratio — content determines height */
      .clickreel-video-wrapper[data-orientation="embed"] {
        aspect-ratio: unset;
      }

      .mobile .clickreel-video-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        aspect-ratio: unset;
      }

      .clickreel-cta-btn {
        display: block;
        width: 100%;
        padding: 14px;
        text-align: center;
        color: white;
        font-weight: 600;
        font-size: 15px;
        text-decoration: none;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      @keyframes clickreel-fade-in {
        from { opacity: 0; } to { opacity: 1; }
      }
      @keyframes clickreel-fade-out {
        from { opacity: 1; } to { opacity: 0; }
      }
      @keyframes clickreel-slide-up {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      /* ================================================================
         Instagram-specific styles
         ================================================================ */

      .clickreel-instagram-video-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        z-index: 1;
      }

      .clickreel-instagram-video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #000;
      }

      .mobile .clickreel-instagram-video-container {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
      }

      .mobile .clickreel-instagram-video {
        width: 100%;
        height: 100%;
        max-height: 100vh;
        max-height: 100dvh;
        object-fit: contain;
      }

      /* Floating actions z-index */
      .clickreel-floating-actions {
        z-index: 50;
        position: relative;
      }

      /* Instagram Embed Container */
      .clickreel-instagram-embed-container {
        position: relative;
        width: 100%;
        background: #000;
        overflow: hidden;
        border-radius: 12px;
        z-index: 1;
      }

      .clickreel-instagram-iframe {
        display: block;
        width: 100%;
        height: 680px;
        max-width: 540px;
        border: none;
        background: #000;
        z-index: 1;
      }

      .mobile .clickreel-instagram-embed-container {
        position: relative;
        width: 100%;
        height: auto;
        z-index: 1;
      }

      .mobile .clickreel-instagram-iframe {
        max-width: 100%;
        height: 680px;
      }

      /* Instagram Fallback */
      .clickreel-instagram-fallback {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        text-align: center;
        padding: 24px;
        height: 100%;
      }

      /* Instagram Loader */
      .clickreel-instagram-loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        z-index: 5;
      }

      .clickreel-instagram-loader-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.2);
        border-top-color: #E1306C;
        border-radius: 50%;
        animation: clickreel-spin 0.8s linear infinite;
      }

      @keyframes clickreel-spin {
        to { transform: rotate(360deg); }
      }

      .clickreel-instagram-controls {
        position: relative;
        opacity: 1;
        background: rgba(0, 0, 0, 0.8);
      }

      .clickreel-instagram-overlay .clickreel-cta-btn {
        background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
      }

      .clickreel-instagram-overlay .clickreel-cta-btn:hover {
        filter: brightness(1.1);
      }

      .clickreel-instagram-overlay .clickreel-floating-action-icon {
        background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
        box-shadow: 0 2px 8px rgba(225, 48, 108, 0.4);
      }

      /* In embed mode, floating actions sit below the video in normal flow */
      .clickreel-instagram-overlay .clickreel-floating-actions {
        position: relative;
        bottom: auto;
        left: auto;
        right: auto;
        width: 100%;
        background: rgba(0, 0, 0, 0.9);
        padding: 8px;
      }

      .clickreel-player-watermark {
        position: absolute;
        bottom: 6px;
        left: 0;
        right: 0;
        text-align: center;
        font-size: 10px;
        z-index: 10;
        color: rgba(255, 255, 255, 0.5);
        text-decoration: none;
        pointer-events: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: color 0.2s ease;
      }
      .clickreel-player-watermark:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    `;
    shadow.appendChild(style);
  }
}
